import * as React from 'react'

const cn = (...classes: Array<string | undefined | false | null>) =>
  classes.filter(Boolean).join(' ')

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'default' | 'secondary' | 'outline' | 'ghost' | 'link'
  size?: 'default' | 'sm' | 'lg' | 'icon'
}

const baseStyles =
  'inline-flex items-center justify-center whitespace-nowrap rounded-full px-6 py-3 text-sm font-semibold shadow-sm transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg)] disabled:pointer-events-none disabled:opacity-50 hover:shadow-md'

const variantStyles: Record<NonNullable<ButtonProps['variant']>, string> = {
  default:
    'bg-[var(--accent)] text-[var(--accent-contrast)] hover:bg-[var(--accent-hover)]',
  secondary:
    'bg-[var(--surface-2)] text-[var(--text-2)] hover:bg-[var(--surface-3)]',
  outline:
    'border border-[var(--border)] text-[var(--text-2)] hover:bg-[var(--surface-2)]',
  ghost: 'text-[var(--text-2)] hover:bg-[var(--surface-2)]',
  link:
    'text-[var(--accent)] hover:text-[var(--accent-hover)] hover:underline underline-offset-4'
}

const sizeStyles: Record<NonNullable<ButtonProps['size']>, string> = {
  default: '',
  sm: 'px-4 py-2 text-xs',
  lg: 'px-7 py-3 text-base',
  icon: 'h-12 w-12 p-0'
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'default', size = 'default', ...props }, ref) => (
    <button
      ref={ref}
      className={cn(baseStyles, variantStyles[variant], sizeStyles[size], className)}
      {...props}
    />
  )
)

Button.displayName = 'Button'

export { Button }
